<template>
  <div class="fp-page">

    <!-- Header -->
    <header class="fp-header">
      <img src="/img/brand/inexlink.png" alt="InexLink" class="fp-logo" />
      <div v-if="ficha" class="fp-header-actions">
        <button type="button" class="fp-btn-pdf" :disabled="exportando" @click="exportarVistaPdf">
          {{ exportando ? $t('fp_generando') : `⬇ ${$t('fp_exportar_pdf')}` }}
        </button>
        <div class="fp-tipo-badge">{{ $t('fp_ficha') }} {{ ficha.tipoFichaNombre }}</div>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading" class="fp-center">
      <div class="fp-spinner"></div>
      <p class="fp-loading-text">{{ $t('fp_cargando_ficha') }}</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="fp-center">
      <div class="fp-error-icon">⚠</div>
      <p class="fp-error-text">{{ error }}</p>
    </div>

    <!-- Contenido -->
    <main v-else-if="ficha" class="fp-container">

      <!-- ── 1. Resumen Ejecutivo ── -->
      <section class="fp-card">
        <h1 v-if="ficha.titulo" class="fp-titulo">{{ ficha.titulo }}</h1>
        <h2 class="fp-section-title">
          <span class="fp-badge">1</span>
          {{ $t('ficha_venta_resumen_ejecutivo') }}
        </h2>
        <p class="fp-resumen">{{ ficha.resumenEjecutivo || '—' }}</p>
      </section>

      <!-- ── 2. Información General y Técnica ── -->
      <section class="fp-card">
        <div class="fp-section-header">
          <h2 class="fp-section-title">
            <span class="fp-badge">2</span>
            {{ $t('ficha_venta_info_general') }}
          </h2>
          <span class="fp-count-badge">
            {{ ficha.infoGeneralYTecnica.length }} {{ $t('registros').toLowerCase() }}
          </span>
        </div>

        <div v-if="!ficha.infoGeneralYTecnica.length" class="fp-empty">
          {{ $t('fp_sin_registros') }}
        </div>

        <div v-else class="fp-table-wrap">
          <table class="fp-table">
            <thead>
              <tr>
                <th v-for="c in columnas" :key="c.key"
                    class="fp-th-ordenable"
                    :class="{ 'fp-td-right': c.align === 'right', 'fp-col-nparte': c.key === 'numeroSerie' }"
                    @click="ordenarPor(c.key)">
                  <input v-if="c.key !== 'idx'" class="fp-th-filtro" v-model="filtros[c.key]"
                         :placeholder="$t('fp_buscar')" @click.stop />
                  <span class="fp-th-label">{{ c.label }} <i :class="iconoOrden(c.key)"></i></span>
                </th>
                <th v-if="hayMultimedia" class="fp-th-multimedia">{{ $t('multimedia') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in filasVisibles" :key="i">
                <td v-for="c in columnas" :key="c.key"
                    :class="{ 'fp-td-right': c.align === 'right', 'fp-col-nparte': c.key === 'numeroSerie' }"
                    :title="c.key === 'numeroSerie' ? celda(c, row, i) : null">
                  {{ celda(c, row, row._idx != null ? row._idx : i) }}
                </td>
                <td v-if="hayMultimedia" class="fp-td-multimedia">
                  <button v-if="cuentaMultimedia(row)" class="fp-btn-multimedia" @click="abrirMultimedia(row)">
                    🖼 {{ cuentaMultimedia(row) }}
                  </button>
                  <span v-else class="fp-sin-multimedia">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </main>

    <!-- Galería del material: fotos y videos cargados desde el portal -->
    <div v-if="multimediaAbierta" class="fp-modal-fondo" @click.self="cerrarMultimedia">
      <div class="fp-modal">
        <div class="fp-modal-head">
          <h3>{{ $t('multimedia') }}<span v-if="materialMultimedia"> — {{ materialMultimedia.partName || materialMultimedia.categoria }}</span></h3>
          <button class="fp-modal-cerrar" @click="cerrarMultimedia">✕</button>
        </div>

        <div class="fp-modal-body">
          <div v-if="fotosDe(materialMultimedia).length" class="fp-galeria">
            <a v-for="foto in fotosDe(materialMultimedia)" :key="foto.id"
               :href="foto.url" target="_blank" rel="noopener" class="fp-foto">
              <img :src="foto.url" :alt="foto.nombre || 'foto'" loading="lazy" />
            </a>
          </div>

          <div v-if="videosDe(materialMultimedia).length" class="fp-videos">
            <template v-for="video in videosDe(materialMultimedia)">
              <iframe v-if="urlEmbed(video.url)" :key="video.id" :src="urlEmbed(video.url)"
                      frameborder="0" allowfullscreen loading="lazy"></iframe>
              <a v-else :key="video.id" :href="video.url" target="_blank" rel="noopener" class="fp-video-link">
                🎬 {{ video.url }}
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="fp-footer">
      <span>© InexLink — {{ $t('fp_footer_tagline') }}</span>
      <a href="https://inexlink.com" target="_blank" rel="noopener">inexlink.com</a>
    </footer>

  </div>
</template>

<script>
import axios from 'axios';

const apiUrl = `${process.env.VUE_APP_API_URL}/api/ficha`;

export default {
  name: 'FichaDetallePublico',
  data() {
    return {
      loading: true,
      error: null,
      ficha: null,
      idFicha: null,
      exportando: false,
      // Filtro por columna (clave = key de la columna) y orden, como en las tablas
      // internas. Todo se resuelve en memoria: la ficha llega completa en una llamada.
      filtros: {},
      ordenColumna: '',
      ordenDireccion: 'asc',
      // Galería del material elegido
      multimediaAbierta: false,
      materialMultimedia: null,
    };
  },
  computed: {
    // Locale para formatear números y fechas según el idioma activo.
    locale() {
      return this.$getLanguage() === 'en' ? 'en-US' : 'es-CL';
    },

    /**
     * Definición única de las columnas visibles (etiqueta traducida + cómo obtener el valor).
     * La usan tanto la tabla HTML como la exportación a PDF, para que nunca diverjan.
     */
    columnas() {
      const c = this.col;
      const tipo4 = this.ficha && this.ficha.idTipoFicha === 4;
      const cols = [{ key: 'idx', label: '#', value: (r, i) => i + 1 }];

      if (c.descCorta) cols.push({ key: 'partName', label: this.$t('descripcion'), value: r => r.partName });
      cols.push({ key: 'categoria', label: this.$t('col_categoria'), value: r => r.categoria });
      if (c.nParte) cols.push({ key: 'numeroSerie', label: this.$t('n_parte'), value: r => r.numeroSerie });
      if (c.fabricante) cols.push({ key: 'fabricante', label: this.$t('fabricante'), value: r => r.fabricante });
      if (c.modelo) cols.push({ key: 'modelo', label: this.$t('modelo'), value: r => r.modelo });
      if (c.anoFab) cols.push({ key: 'anoFabricacion', label: this.$t('ano_fabricacion'), value: r => r.anoFabricacion });
      if (c.horasOp) cols.push({ key: 'horasOperacion', label: this.$t('horas_operacion'), value: r => r.horasOperacion });
      if (c.dimensiones) cols.push({
        key: 'dimensiones',
        label: tipo4 ? this.$t('tamano') : this.$t('dimensiones_mm'),
        value: r => r.dimensiones,
      });
      if (c.tipo) cols.push({ key: 'tipo', label: this.$t('tipo'), value: r => r.tipo });
      cols.push({
        key: 'cantidad',
        label: this.$t('cluster_cantidad'),
        align: 'right',
        value: r => (r.cantidad != null && r.cantidad !== 0 ? Number(r.cantidad).toLocaleString(this.locale) : null),
      });
      if (c.unidad) cols.push({ key: 'unidadDeMedida', label: this.$t('unidad_medida'), value: r => r.unidadDeMedida });
      cols.push({ key: 'condicion', label: this.$t('col_condicion'), value: r => this.condicionLabel(r.condicion) });
      if (c.tamanoNeum) cols.push({
        key: 'tamanoCondicionNeumaticos',
        label: this.$t('tamano_condicion_neumaticos'),
        value: r => r.tamanoCondicionNeumaticos,
      });
      if (c.histMant) cols.push({
        key: 'historialMantenimiento',
        label: this.$t('historial_mantenimiento'),
        value: r => r.historialMantenimiento,
      });
      if (c.reparaciones) cols.push({
        key: 'reparacionesRevisionesRecientes',
        label: this.$t('reparaciones_recientes'),
        value: r => r.reparacionesRevisionesRecientes,
      });
      if (c.usoRec) cols.push({ key: 'usoRecomendado', label: this.$t('uso_recomendado'), value: r => r.usoRecomendado });
      if (c.sistComp) cols.push({
        key: 'sistemasCompatibles',
        label: tipo4 ? this.$t('fp_equipo_compatible') : this.$t('sistemas_compatibles'),
        value: r => r.sistemasCompatibles,
      });
      if (c.profBanda) cols.push({ key: 'profundidadBanda', label: this.$t('profundidad_banda'), value: r => r.profundidadBanda });

      return cols;
    },

    /** Alguna fila trae fotos o videos: sin eso la columna no se muestra. */
    hayMultimedia() {
      const rows = (this.ficha && this.ficha.infoGeneralYTecnica) || [];
      return rows.some(r => this.cuentaMultimedia(r) > 0);
    },

    /**
     * Filas tras aplicar los filtros de la cabecera y el orden. Se conserva _idx con la
     * posición original para que la columna "#" no se renumere al filtrar u ordenar.
     */
    filasVisibles() {
      let filas = (this.ficha && this.ficha.infoGeneralYTecnica) || [];

      const activos = Object.keys(this.filtros).filter(k => String(this.filtros[k] || '').trim() !== '');
      if (activos.length) {
        const porKey = {};
        this.columnas.forEach(c => { porKey[c.key] = c; });
        filas = filas.filter(row => activos.every(k => {
          const col = porKey[k];
          if (!col) return true;
          const texto = String(this.celda(col, row, row._idx) || '').toLowerCase();
          return texto.includes(String(this.filtros[k]).trim().toLowerCase());
        }));
      }

      if (this.ordenColumna) {
        const col = this.columnas.find(c => c.key === this.ordenColumna);
        if (col) {
          const dir = this.ordenDireccion === 'asc' ? 1 : -1;
          filas = filas.slice().sort((a, b) => {
            const va = col.value(a, a._idx);
            const vb = col.value(b, b._idx);
            const na = parseFloat(va);
            const nb = parseFloat(vb);
            if (!isNaN(na) && !isNaN(nb)) return (na - nb) * dir;
            return String(va == null ? '' : va).localeCompare(String(vb == null ? '' : vb), this.locale) * dir;
          });
        }
      }

      return filas;
    },

    col() {
      const rows = this.ficha && this.ficha.infoGeneralYTecnica || [];
      if (!rows.length) return {};
      const first = rows[0];
      const tipo = this.ficha ? this.ficha.idTipoFicha : 1;
      return {
        descCorta:   rows.some(r => r.partName && r.partName !== '??'),
        nParte:      rows.some(r => r.numeroSerie && r.numeroSerie !== '??'),
        fabricante:  rows.some(r => r.fabricante && r.fabricante !== '??'),
        modelo:      first.visibleModelo,
        anoFab:      rows.some(r => r.anoFabricacion && r.anoFabricacion !== '??'),
        horasOp:     rows.some(r => r.horasOperacion && r.horasOperacion !== '??'),
        unidad:      rows.some(r => r.unidadDeMedida),
        dimensiones: tipo === 3 || tipo === 4 ? first.visibleDimensiones : false,
        tipo:        tipo === 3 || tipo === 4 ? first.visibleTipo : false,
        tamanoNeum:  tipo === 1 ? first.visibleTamanoCondicionNeumaticos : false,
        histMant:    tipo === 1 ? first.visibleHistorialMantenimiento : false,
        reparaciones:tipo === 1 ? first.visibleReparacionesRevisionesRecientes : false,
        usoRec:      tipo === 3 ? first.visibleUsoRecomendado : false,
        sistComp:    (tipo === 3 || tipo === 4) ? first.visibleSistemasCompatibles : false,
        profBanda:   tipo === 4 ? first.visibleProfundidadBanda : false,
      };
    },
  },
  methods: {
    condicionLabel(cond) {
      if (!cond) return '—';
      const map = {
        N: 'cond_nuevo', U: 'cond_usado', R: 'cond_reparado', E: 'fp_cond_excedente',
        new: 'cond_nuevo', used: 'cond_usado', repaired: 'cond_reparado',
      };
      return map[cond] ? this.$t(map[cond]) : cond;
    },

    ordenarPor(key) {
      if (key === 'idx') return;
      if (this.ordenColumna === key) {
        this.ordenDireccion = this.ordenDireccion === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenColumna = key;
        this.ordenDireccion = 'asc';
      }
    },

    iconoOrden(key) {
      if (key === 'idx') return '';
      if (this.ordenColumna !== key) return 'fp-sort';
      return this.ordenDireccion === 'asc' ? 'fp-sort fp-sort-asc' : 'fp-sort fp-sort-desc';
    },

    /** Fotos + videos que tiene el material. */
    cuentaMultimedia(row) {
      return ((row && row.multimedia) || []).length;
    },

    abrirMultimedia(row) {
      this.materialMultimedia = row;
      this.multimediaAbierta = true;
    },

    cerrarMultimedia() {
      this.multimediaAbierta = false;
      this.materialMultimedia = null;
    },

    fotosDe(row) {
      return ((row && row.multimedia) || []).filter(m => m.idTipoArchivo === 2);
    },

    videosDe(row) {
      return ((row && row.multimedia) || []).filter(m => m.idTipoArchivo === 3);
    },

    /**
     * URL embebible de YouTube. Los videos se guardan como enlace normal
     * (watch?v=… o youtu.be/…), que no se puede montar en un iframe.
     */
    urlEmbed(url) {
      const texto = String(url || '');
      const match = texto.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
      return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    },

    // Valor a mostrar en una celda, con guion largo cuando viene vacío.
    celda(col, row, i) {
      const v = col.value(row, i);
      return v === null || v === undefined || v === '' ? '—' : v;
    },


    /**
     * La tabla tal como se ve: solo las filas que dejaron los filtros, en el orden elegido
     * y con las columnas visibles. Se envía ya formateada al backend, que arma el PDF: el
     * filtro compara contra el texto de cada celda y el orden depende del idioma, así que
     * rehacer eso en el servidor duplicaría la lógica y las dos copias divergirían.
     */
    buildTableData() {
      const filas = this.filasVisibles;
      const head = this.columnas.map(c => c.label);
      // Cada celda va como texto: la columna "#" y las numéricas devuelven number, y el
      // backend espera cadenas. System.Text.Json no convierte number a string y rechaza
      // el cuerpo completo con 400 antes de entrar al método.
      const body = filas.map(row => this.columnas.map(c => String(this.celda(c, row, row._idx))));
      // Id del material de cada fila, en la misma posición, para resolver su portada.
      const idsMaterial = filas.map(row => row.id || row.idMaterial || 0);
      // Las claves acompañan a las etiquetas para que el PDF reparta el ancho de cada
      // columna igual que la tabla: los rótulos vienen traducidos y no identifican nada.
      const keys = this.columnas.map(c => c.key);
      return { head, body, idsMaterial, keys };
    },


    /**
     * El PDF lo genera el backend: las fotos viven en un blob sin CORS, así que el navegador
     * no puede incrustarlas (fetch bloqueado y canvas contaminado). El servidor las descarga,
     * las reduce y las dibuja bajo cada fila.
     */
    async exportarVistaPdf() {
      if (!this.ficha) return;
      this.exportando = true;
      try {
        const { head, body, idsMaterial, keys } = this.buildTableData();
        const resp = await fetch(`${apiUrl}/publica/${this.idFicha}/pdf`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ head, body, idsMaterial, keys, lang: this.$getLanguage() }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        // El PDF llega como binario en la respuesta del POST, así que la descarga se
        // dispara desde un blob en memoria en vez de con un enlace directo.
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Ficha_${this.idFicha}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('exportarVistaPdf error:', e);
        alert(this.$t('fp_error_pdf'));
      } finally {
        this.exportando = false;
      }
    },
  },
  async created() {
    const idFicha = this.$route.params.idFicha;
    this.idFicha = idFicha;

    // El idioma llega por query string desde el link del PDF (?lang=es|en); main.js ya lo
    // persistió en el translator antes de montar la app. Se reenvía a la API para que los
    // datos del catálogo (categorías, condiciones, etc.) vengan en el mismo idioma.
    const lang = this.$getLanguage();

    try {
      const { data } = await axios.get(`${apiUrl}/publica/${idFicha}`, { params: { lang } });
      if (data && data.success) {
        // _idx conserva la posición original: la columna "#" no debe renumerarse
        // cuando el usuario filtra u ordena la tabla.
        const filas = (data.data && data.data.infoGeneralYTecnica) || [];
        filas.forEach((fila, i) => { fila._idx = i; });
        this.ficha = data.data;
      } else {
        this.error = this.$t('fp_error_cargar');
      }
    } catch (e) {
      this.error = e.response && e.response.status === 404
        ? this.$t('fp_ficha_no_encontrada')
        : this.$t('fp_error_datos');
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
* { box-sizing: border-box; }

.fp-page {
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Inter', 'Segoe UI', sans-serif;
  color: #1e293b;
  display: flex;
  flex-direction: column;
}

/* ── Header ── */
.fp-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 32px;
  background: #fff;
  border-bottom: 2px solid #00b8d4;
  box-shadow: 0 1px 4px rgba(0,0,0,.06);
}
.fp-logo { height: 38px; object-fit: contain; }
.fp-header-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
}
.fp-tipo-badge {
  background: #00b8d4;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  letter-spacing: .04em;
  text-transform: uppercase;
}
.fp-btn-pdf {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #00b8d4;
  border: 1.5px solid #00b8d4;
  font-size: .82rem;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 20px;
  text-decoration: none;
  cursor: pointer;
  font-family: inherit;
  transition: background .15s, color .15s;
}
.fp-btn-pdf:hover:not(:disabled) {
  background: #00b8d4;
  color: #fff;
  text-decoration: none;
}
.fp-btn-pdf:disabled {
  opacity: .6;
  cursor: default;
}

/* ── Loading / Error ── */
.fp-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}
.fp-spinner {
  width: 44px; height: 44px;
  border: 4px solid #e2e8f0;
  border-top-color: #00b8d4;
  border-radius: 50%;
  animation: fp-spin .8s linear infinite;
}
@keyframes fp-spin { to { transform: rotate(360deg); } }
.fp-loading-text { margin-top: 14px; color: #64748b; font-size: .9rem; }
.fp-error-icon { font-size: 3rem; margin-bottom: 10px; }
.fp-error-text { color: #ef4444; font-size: .95rem; }

/* ── Container ── */
.fp-container {
  flex: 1;
  max-width: 1200px;
  width: 100%;
  margin: 32px auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ── Cards ── */
.fp-card {
  background: #fff;
  border-radius: 10px;
  padding: 28px 32px;
  box-shadow: 0 1px 6px rgba(0,0,0,.07);
}
.fp-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.fp-titulo {
  font-size: 1.4rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 12px;
}
.fp-section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 18px;
}
.fp-section-header .fp-section-title { margin-bottom: 0; }
.fp-badge {
  background: #00b8d4;
  color: #fff;
  width: 24px; height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: .78rem;
  font-weight: 800;
  flex-shrink: 0;
}
.fp-count-badge {
  margin-left: auto;
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
  font-size: .76rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 12px;
}

/* ── Resumen ── */
.fp-resumen {
  white-space: pre-wrap;
  line-height: 1.7;
  color: #334155;
  font-size: .95rem;
  margin: 0;
}

/* ── Table ── */
.fp-table-wrap {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}
.fp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: .82rem;
  min-width: 640px;
}
.fp-table thead tr {
  background: #f1f5f9;
}
.fp-table th {
  padding: 9px 12px;
  font-weight: 700;
  color: #475569;
  text-align: left;
  white-space: nowrap;
  border-bottom: 2px solid #e2e8f0;
}
.fp-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  vertical-align: middle;
}
.fp-table tbody tr:last-child td { border-bottom: none; }
.fp-table tbody tr:hover { background: #f8fafc; }
.fp-td-right { text-align: right !important; }
/* ── Cabecera con filtro y orden ── */
.fp-th-ordenable { cursor: pointer; user-select: none; vertical-align: top; }
.fp-th-filtro {
  width: 100%;
  font-size: 11px;
  font-weight: 400;
  padding: 2px 5px;
  margin-bottom: 4px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  display: block;
}
.fp-th-label { display: block; white-space: nowrap; }
.fp-sort::after { content: ' ⇅'; color: #94a3b8; font-size: .8em; }
.fp-sort-asc::after { content: ' ↑'; color: #0f172a; }
.fp-sort-desc::after { content: ' ↓'; color: #0f172a; }

/* ── Columna y modal de multimedia ── */
.fp-th-multimedia, .fp-td-multimedia { text-align: center; white-space: nowrap; }
.fp-btn-multimedia {
  background: #00b8d4;
  border: none;
  border-radius: 6px;
  color: #fff;
  cursor: pointer;
  font-size: .78rem;
  font-weight: 600;
  padding: 4px 10px;
}
.fp-btn-multimedia:hover { background: #0097a7; }
.fp-sin-multimedia { color: #cbd5e1; }

.fp-modal-fondo {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, .65);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1050;
}
.fp-modal {
  background: #fff;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.fp-modal-head {
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  padding: 14px 20px;
}
.fp-modal-head h3 { font-size: 1rem; margin: 0; }
.fp-modal-cerrar {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
}
.fp-modal-body { overflow-y: auto; padding: 18px 20px; }
.fp-galeria {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
.fp-foto img {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  height: 150px;
  object-fit: cover;
  width: 100%;
}
.fp-videos { display: grid; gap: 12px; margin-top: 16px; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); }
.fp-videos iframe { aspect-ratio: 16 / 9; border-radius: 8px; width: 100%; }
.fp-video-link { color: #0f766e; font-size: .85rem; word-break: break-all; }

/* N° Parte: el valor puede llegar como una cadena larguísima sin espacios (números de
   200 dígitos). Se fija el ancho en 200px —con min-width, porque al cortar por carácter
   el contenido deja de empujar la columna y el navegador la colapsa— y el texto se parte
   en varias líneas. El white-space pisa el nowrap que llevan todos los th. */
.fp-col-nparte {
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  white-space: normal !important;
  word-break: break-all;
  overflow-wrap: anywhere;
}
.fp-empty {
  color: #94a3b8;
  font-size: .9rem;
  padding: 20px 0;
}

/* ── Footer ── */
.fp-footer {
  text-align: center;
  padding: 18px 24px;
  font-size: .8rem;
  color: #94a3b8;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: center;
  gap: 24px;
}
.fp-footer a {
  color: #00b8d4;
  text-decoration: none;
  font-weight: 600;
}
.fp-footer a:hover { text-decoration: underline; }

@media (max-width: 640px) {
  .fp-header { padding: 14px 16px; flex-wrap: wrap; row-gap: 10px; }
  .fp-header-actions { gap: 8px; }
  .fp-btn-pdf { font-size: .76rem; padding: 5px 10px; }
  .fp-container { padding: 0 12px; margin: 16px auto; }
  .fp-card { padding: 18px 16px; }
}
</style>
